import { createFileRoute, Link, Outlet, useRouterState, useNavigate, useLocation } from '@tanstack/react-router'
import { useEvent, useVendor, useVendorStats, useVendorCountStats, useVendorLocations, useCreateVendorAdministrator, useDeleteVendorAdministrator, useUpdateVendorAdministrator, usePermissionGroups, useUpdateVendor, useOrderReviewSummary, useItemReviews, useItemReviewSummary, useSendActivationCode } from '@/lib/api/queries'
import { DEFAULT_EVENT_TIMEZONE, eventLocalToServerNaive } from '@/lib/eventTime'
import { DateFilter } from '@/components/ui/date-filter'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
  ArrowLeft, 
  Pencil, 
  Users, 
  Package, 
  ShoppingCart, 
  BarChart2, 
  DollarSign,
  Plus,
  MapPin,
  UserPlus,
  X,
  ChevronDown,
  Trash2,
  Send,
  Mail,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  KeyRound,
  Star,
  ThumbsUp,
  MessageSquare,
  Upload
} from 'lucide-react'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import type { VendorEvent, VendorAdministrator, Vendor, PermissionGroup } from '@/lib/api/types'
import { cn } from '@/lib/utils'
import { useState, useMemo, useEffect, useRef } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts'
import { format } from 'date-fns'
import { ExportLoginPdfModal } from '@/components/export-login-pdf-modal'
import { EditLocationModal } from './vendors.$vendorId.locations.$locationId'
import { NewLocationModal } from './vendors'

// Format group name: "vendor_admin" -> "Vendor Admin"
const formatGroupName = (name: string) => {
  return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

// Convert 24hr to 12hr format: "15:00" -> "3 PM", 15 -> "3 PM"
const formatHour12 = (hour: number | string): string => {
  const h = typeof hour === 'string' ? parseInt(hour.split(':')[0]) : hour
  if (h === 0) return '12 AM'
  if (h === 12) return '12 PM'
  if (h < 12) return `${h} AM`
  return `${h - 12} PM`
}

export const Route = createFileRoute('/_authenticated/events/$eventId/vendors/$vendorId')({
  component: VendorDetailLayout,
})

// Layout wrapper that renders either the vendor detail or location detail
function VendorDetailLayout() {
  const matches = useRouterState({ select: (s) => s.matches })
  
  // Check if this is the deepest match (no child routes active)
  const lastMatch = matches[matches.length - 1]
  const isVendorDetailPage = lastMatch?.routeId === '/_authenticated/events/$eventId/vendors/$vendorId'
  
  if (isVendorDetailPage) {
    return <VendorDetailPage />
  }
  
  return <Outlet />
}

// Tab types for vendor detail page
type VendorTab = 'overview' | 'locations' | 'staff' | 'reporting' | 'reviews'

function VendorDetailPage() {
  const { eventId, vendorId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const vendorIdNum = Number(vendorId)
  const navigate = useNavigate()
  const locationHash = useLocation().hash
  
  // Tab state - initialize from URL hash
  const [activeTab, setActiveTab] = useState<VendorTab>(() => {
    const hash = locationHash.replace('#', '')
    if (hash === 'locations' || hash === 'staff' || hash === 'reporting' || hash === 'reviews') return hash
    return 'overview'
  })
  
  // Sync tab with URL hash
  useEffect(() => {
    if (locationHash.replace('#', '') !== activeTab) {
      navigate({ hash: activeTab, replace: true })
    }
  }, [activeTab, locationHash, navigate])
  
  const handleTabChange = (tab: VendorTab) => {
    setActiveTab(tab)
  }
  
  // Permission checks
  const canViewVendors = usePermission('can_view_vendors')
  const canEditVendors = usePermission('can_edit_vendors')
  const canCreateVendors = usePermission('can_create_vendors')
  const canAddVendorAdmin = usePermission('can_add_vendor_admin')
  const canChangeVendorAdmin = usePermission('change_vendoradministrator')
  const canExportVendorLogins = usePermission('can_export_vendor_logins')
  
  // Modal state
  const [showAddStaffModal, setShowAddStaffModal] = useState(false)
  const [showEditVendorModal, setShowEditVendorModal] = useState(false)
  const [editingLocation, setEditingLocation] = useState<VendorEvent | null>(null)
  const [showNewLocationModal, setShowNewLocationModal] = useState(false)
  const [showExportLoginPdfModal, setShowExportLoginPdfModal] = useState(false)
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false)
  const optionsDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (optionsDropdownRef.current && !optionsDropdownRef.current.contains(event.target as Node)) {
        setShowOptionsDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  // Item reporting sort state
  type SortField = 'item_name' | 'booth' | 'order_count' | 'quantity_sold' | 'token_revenue' | 'cash_revenue'
  const [sortField, setSortField] = useState<SortField>('quantity_sold')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Item reporting date window (datetime-local, event-local time)
  const [reportFrom, setReportFrom] = useState('')
  const [reportTo, setReportTo] = useState('')
  
  // Permission guard
  if (!canViewVendors) {
    return <AccessDenied message="You don't have permission to view vendors." />
  }
  
  // Fetch vendor details with expanded vendorevent_set and administrators, plus all_items for reporting
  const { data: vendor, isLoading: vendorLoading } = useVendor(vendorIdNum, {
    expand: 'vendorevent_set,administrators,administrators.user,administrators.groups',
    aggregates: true,
    all_items: true,
    event: eventIdNum,
  })

  // Event timezone for interpreting the item-reporting date window
  const { data: eventData } = useEvent(eventIdNum)

  // Server-naive date params for the item-reporting window; undefined when
  // no filter is set so the unfiltered all_items from `vendor` is used.
  const reportDateParams = useMemo(() => {
    if (!reportFrom && !reportTo) return undefined
    const tz = eventData?.timezone || DEFAULT_EVENT_TIMEZONE
    const params: { created_at__gte?: string; created_at__lte?: string } = {}
    if (reportFrom) params.created_at__gte = eventLocalToServerNaive(reportFrom, tz)
    if (reportTo) params.created_at__lte = eventLocalToServerNaive(reportTo, tz)
    return params
  }, [reportFrom, reportTo, eventData?.timezone])

  // Date-scoped copy of all_items — fetched only while a window is set, so
  // the main vendor query (page chrome) never refetches on filter edits.
  const { data: datedVendor, isFetching: reportFiltering } = useVendor(
    vendorIdNum,
    { aggregates: false, all_items: true, event: eventIdNum, ...reportDateParams },
    { enabled: !!reportDateParams },
  )
  
  // Fetch vendor stats (legacy endpoint for time_data chart)
  const { data: stats, isLoading: statsLoading } = useVendorStats(vendorIdNum, eventIdNum)
  
  // Fetch new vendor count stats with top items and hourly data
  const { data: countStats } = useVendorCountStats(vendorIdNum, eventIdNum, { top_count: 10 })
  
  // Fetch vendor locations
  const { data: locationsData, isLoading: locationsLoading } = useVendorLocations(vendorIdNum, eventIdNum)
  
  const locations = locationsData?.results || vendor?.vendorevent_set || []
  // Use administrators from vendor detail (already fetched with expand)
  const administrators = vendor?.administrators || []
  const isLoading = vendorLoading || statsLoading

  // Fetch item reviews for this vendor to show ratings in item reporting table
  const { data: vendorItemReviewsData } = useItemReviews({
    vendor: vendorIdNum,
    event: eventIdNum,
    expand: 'vendor_event_item',
    page_size: 200,
  })

  // Build a map of vendor_event_item_id -> aggregated review data
  const vendorItemRatingsMap = useMemo(() => {
    const map = new Map<number, { totalRatings: number; scaleSum: number; scaleCount: number; thumbsUp: number; thumbsDown: number }>()
    if (!vendorItemReviewsData?.results) return map
    for (const review of vendorItemReviewsData.results) {
      const itemId = typeof review.vendor_event_item === 'object' ? review.vendor_event_item.id : review.vendor_event_item
      if (!map.has(itemId)) {
        map.set(itemId, { totalRatings: 0, scaleSum: 0, scaleCount: 0, thumbsUp: 0, thumbsDown: 0 })
      }
      const entry = map.get(itemId)!
      entry.totalRatings++
      for (const cr of review.criterion_ratings || []) {
        if (cr.scale_rating != null) {
          entry.scaleSum += cr.scale_rating
          entry.scaleCount++
        }
        if (cr.thumbs_up != null) {
          if (cr.thumbs_up) entry.thumbsUp++
          else entry.thumbsDown++
        }
      }
    }
    return map
  }, [vendorItemReviewsData?.results])

  // Stats helpers - use new countStats when available, fallback to legacy stats
  const getCustomerCount = () => {
    const customers = stats?.customers
    if (Array.isArray(customers) && customers.length > 0) {
      return customers[0]
    }
    return stats?.customer_count || stats?.customers || 0
  }
  const getTotalOrders = () => countStats?.totals?.total_orders || stats?.total_orders || 0
  const getItemsSold = () => countStats?.totals?.total_items || stats?.items || 0
  const getAvgOrderValue = () => {
    const tokensSpent = countStats?.totals?.total_tokens || stats?.tokens_spent || 0
    const totalOrders = countStats?.totals?.total_orders || stats?.total_orders || 0
    if (totalOrders > 0) {
      return Math.round((tokensSpent / totalOrders) * 10) / 10
    }
    return 0
  }
  const getTokenRevenue = () => countStats?.totals?.total_tokens || stats?.tokens_spent || 0
  const getCashRevenue = () => countStats?.totals?.total_amount || 0

  // Chart data from time_data (legacy endpoint)
  const chartData = useMemo(() => {
    const timeData = stats?.time_data || []
    return timeData.map(entry => {
      let formattedDate = entry.date
      try {
        // Parse date format from '2024-08-17 20' to 'Aug 17 20:00'
        const dateObj = new Date(entry.date.replace(' ', 'T') + ':00:00')
        formattedDate = format(dateObj, 'MMM dd HH:00')
      } catch {
        // Keep original if parsing fails
      }
      return {
        date: formattedDate,
        amount: entry.amount || 0,
      }
    })
  }, [stats?.time_data])

  // Hourly sales data from new endpoint with 12hr format
  const hourlyData = useMemo(() => {
    return (countStats?.sales_by_hour || []).map(entry => ({
      ...entry,
      hour_label_12: formatHour12(entry.hour)
    }))
  }, [countStats?.sales_by_hour])

  // Top items data from new endpoint
  const topItems = useMemo(() => {
    return countStats?.top_items || []
  }, [countStats?.top_items])

  // Sorted item reporting data — date-scoped rows when a window is set
  // (falling back to the unfiltered rows while the scoped fetch loads,
  // dimmed via reportFiltering).
  const sortedItems = useMemo(() => {
    const items = (reportDateParams ? datedVendor?.all_items ?? vendor?.all_items : vendor?.all_items) || []
    return [...items].sort((a, b) => {
      const aVal = a[sortField]
      const bVal = b[sortField]
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal)
      }
      return sortDirection === 'asc' 
        ? (aVal as number) - (bVal as number) 
        : (bVal as number) - (aVal as number)
    })
  }, [vendor?.all_items, datedVendor?.all_items, reportDateParams, sortField, sortDirection])

  // Toggle sort function
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  // Sort icon helper
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />
  }

  if (isLoading) {
    return (
      <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-6 w-32 bg-neutral-200 dark:bg-neutral-800 rounded" />
          <div className="h-10 w-64 bg-neutral-200 dark:bg-neutral-800 rounded" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-24 bg-neutral-200 dark:bg-neutral-800 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (!vendor) {
    return (
      <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-8">
        <p className="text-neutral-500">Vendor not found</p>
      </div>
    )
  }

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-8">
      {/* Back Button and Edit (mobile) */}
      <div className="mb-6 flex items-center justify-between">
        <Link
          to="/events/$eventId/vendors"
          params={{ eventId }}
          className="flex items-center text-sm text-blue-600 dark:text-blue-500 hover:text-blue-800 dark:hover:text-blue-400"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Vendors
        </Link>
      </div>

      {/* Vendor Title and Actions Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center">
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            {vendor.name}
          </h1>
          <span className={cn(
            'mt-2 sm:mt-0 sm:ml-3 px-2 py-1 text-xs rounded-full inline-flex w-fit',
            vendor.is_approved
              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
          )}>
            {vendor.is_approved ? 'Approved' : 'Not Approved'}
          </span>
        </div>
        {(canEditVendors || canExportVendorLogins) && (
          <div className="relative" ref={optionsDropdownRef}>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setShowOptionsDropdown(!showOptionsDropdown)}
            >
              Options
              <ChevronDown className={cn('w-4 h-4 transition-transform', showOptionsDropdown && 'rotate-180')} />
            </Button>
            {showOptionsDropdown && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-neutral-900 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-800 py-1 min-w-[200px]">
                {canExportVendorLogins && (
                  <button
                    onClick={() => { setShowExportLoginPdfModal(true); setShowOptionsDropdown(false) }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <KeyRound className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                    Export Login PDF
                  </button>
                )}
                {canEditVendors && (
                  <button
                    onClick={() => { setShowEditVendorModal(true); setShowOptionsDropdown(false) }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <Pencil className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
                    Edit Vendor
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex space-x-4 sm:space-x-6">
          <button
            onClick={() => handleTabChange('overview')}
            className={cn(
              'pb-3 text-xs sm:text-sm font-medium border-b-2 transition-colors',
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200'
            )}
          >
            Overview
          </button>
          <button
            onClick={() => handleTabChange('locations')}
            className={cn(
              'pb-3 text-xs sm:text-sm font-medium border-b-2 transition-colors',
              activeTab === 'locations'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200'
            )}
          >
            Locations ({locations.length})
          </button>
          <button
            onClick={() => handleTabChange('staff')}
            className={cn(
              'pb-3 text-xs sm:text-sm font-medium border-b-2 transition-colors',
              activeTab === 'staff'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200'
            )}
          >
            Vendor Staff
          </button>
          <button
            onClick={() => handleTabChange('reporting')}
            className={cn(
              'pb-3 text-xs sm:text-sm font-medium border-b-2 transition-colors',
              activeTab === 'reporting'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200'
            )}
          >
            Item Reporting
          </button>
          <button
            onClick={() => handleTabChange('reviews')}
            className={cn(
              'pb-3 text-xs sm:text-sm font-medium border-b-2 transition-colors',
              activeTab === 'reviews'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200'
            )}
          >
            Reviews
          </button>
        </div>
      </div>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {/* Customers Card */}
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 flex items-start">
          <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-900 mr-3">
            <Users className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
          </div>
          <div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Customers</p>
            <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
              {getCustomerCount().toLocaleString()}
            </p>
          </div>
        </div>
        
        {/* Orders Card */}
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 flex items-start">
          <div className="p-2 rounded-full bg-pink-100 dark:bg-pink-900/20 mr-3">
            <Package className="h-5 w-5 text-pink-500 dark:text-pink-400" />
          </div>
          <div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Orders</p>
            <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
              {getTotalOrders().toLocaleString()}
            </p>
          </div>
        </div>
        
        {/* Items Card */}
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 flex items-start">
          <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/20 mr-3">
            <ShoppingCart className="h-5 w-5 text-amber-500 dark:text-amber-400" />
          </div>
          <div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Items</p>
            <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
              {getItemsSold().toLocaleString()}
            </p>
          </div>
        </div>
        
        {/* Avg Order Card */}
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 flex items-start">
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/20 mr-3">
            <BarChart2 className="h-5 w-5 text-blue-500 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Avg. Order</p>
            <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
              {getAvgOrderValue()}
            </p>
          </div>
        </div>
        
        {/* Revenue Card */}
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 flex items-start">
          <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/20 mr-3">
            <DollarSign className="h-5 w-5 text-green-500 dark:text-green-400" />
          </div>
          <div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Token Revenue</p>
            <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
              {getTokenRevenue().toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="mb-8 w-full bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
        <div className="px-6 pt-6">
          <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{vendor.name} Revenue</h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Hourly revenue trends in tokens</p>
        </div>
        <div className="px-4 py-6">
          {chartData.length > 0 ? (
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    angle={-45} 
                    textAnchor="end" 
                    height={70}
                    tick={{ fontSize: 10, fontWeight: 500 }}
                    stroke="#9ca3af"
                    tickMargin={8}
                  />
                  <YAxis 
                    stroke="#9ca3af" 
                    tick={{ fontSize: 10, fontWeight: 600 }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={false}
                    name="Revenue (Tokens)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[400px] bg-neutral-50 dark:bg-neutral-900 rounded flex items-center justify-center">
              <p className="text-neutral-400">No revenue data available</p>
            </div>
          )}
        </div>
      </div>

      {/* New Charts Row - Top Items and Sales by Hour */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Selling Items */}
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">Top Selling Items</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Best performers by quantity sold</p>
          </div>
          <div className="px-6 pb-6">
            {topItems.length > 0 ? (
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={topItems.slice(0, 8)} 
                    layout="vertical" 
                    margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={true} vertical={false} />
                    <XAxis 
                      type="number" 
                      stroke="#9ca3af" 
                      tick={{ fontSize: 11, fontWeight: 500 }}
                      tickFormatter={(value) => value.toLocaleString()}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="item_name" 
                      stroke="#9ca3af" 
                      tick={{ fontSize: 11, fontWeight: 500 }}
                      width={110}
                      tickMargin={8}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                      formatter={(value, name) => {
                        if (name === 'quantity_sold') return [(value ?? 0).toLocaleString(), 'Qty Sold']
                        return [(value ?? 0).toLocaleString(), String(name)]
                      }}
                    />
                    <Bar dataKey="quantity_sold" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                      {topItems.slice(0, 8).map((_, index) => (
                        <Cell key={`cell-${index}`} fill={index === 0 ? '#22c55e' : '#3b82f6'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[320px] bg-neutral-50 dark:bg-neutral-900 rounded flex items-center justify-center">
                <p className="text-neutral-400">No item data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Sales by Hour */}
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
          <div className="px-6 pt-6 pb-2 flex justify-between items-start">
            <div>
              <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">Sales by Hour</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Order distribution throughout the day</p>
            </div>
            {countStats?.top_hour && (
              <div className="text-right">
                <p className="text-xs text-neutral-500 dark:text-neutral-400">Peak Hour</p>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatHour12(countStats.top_hour.hour)}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">{countStats.top_hour.order_count.toLocaleString()} orders</p>
              </div>
            )}
          </div>
          <div className="px-6 pb-6">
            {hourlyData.length > 0 ? (
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourlyData} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis 
                      dataKey="hour_label_12" 
                      stroke="#9ca3af" 
                      tick={{ fontSize: 10, fontWeight: 500 }}
                      angle={-45}
                      textAnchor="end"
                      height={55}
                      tickMargin={5}
                    />
                    <YAxis 
                      stroke="#9ca3af" 
                      tick={{ fontSize: 11, fontWeight: 500 }}
                      tickFormatter={(value) => value.toLocaleString()}
                      tickMargin={8}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                      formatter={(value, name) => {
                        if (name === 'order_count') return [(value ?? 0).toLocaleString(), 'Orders']
                        if (name === 'items_sold') return [(value ?? 0).toLocaleString(), 'Items']
                        if (name === 'token_revenue') return [(value ?? 0).toLocaleString(), 'Tokens']
                        return [(value ?? 0).toLocaleString(), String(name)]
                      }}
                    />
                    <Bar dataKey="order_count" fill="#8b5cf6" radius={[4, 4, 0, 0]}>
                      {hourlyData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={countStats?.top_hour && entry.hour === countStats.top_hour.hour ? '#22c55e' : '#8b5cf6'} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[320px] bg-neutral-50 dark:bg-neutral-900 rounded flex items-center justify-center">
                <p className="text-neutral-400">No hourly data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats Row */}
      {countStats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {/* Best Selling Item */}
          {topItems[0] && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-green-600 dark:text-green-400">🔥</span>
                <p className="text-xs font-medium text-green-700 dark:text-green-300 uppercase">Best Seller</p>
              </div>
              <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 truncate">{topItems[0].item_name}</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">{topItems[0].quantity_sold.toLocaleString()} sold</p>
            </div>
          )}

          {/* Peak Hour */}
          {countStats.top_hour && (
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-purple-600 dark:text-purple-400">⏰</span>
                <p className="text-xs font-medium text-purple-700 dark:text-purple-300 uppercase">Peak Hour</p>
              </div>
              <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{formatHour12(countStats.top_hour.hour)}</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">{countStats.top_hour.order_count.toLocaleString()} orders</p>
            </div>
          )}

          {/* Cash Revenue */}
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-amber-600 dark:text-amber-400">💰</span>
              <p className="text-xs font-medium text-amber-700 dark:text-amber-300 uppercase">Cash Revenue</p>
            </div>
            <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">${getCashRevenue().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">Total cash sales</p>
          </div>

          {/* Items per Order */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-blue-600 dark:text-blue-400">📦</span>
              <p className="text-xs font-medium text-blue-700 dark:text-blue-300 uppercase">Items/Order</p>
            </div>
            <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">
              {getTotalOrders() > 0 ? (getItemsSold() / getTotalOrders()).toFixed(1) : '0'}
            </p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">Avg items per order</p>
          </div>
        </div>
      )}
        </>
      )}

      {/* Locations Tab Content */}
      {activeTab === 'locations' && (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">Vendor Locations</h2>
          {canCreateVendors && (
            <Button className="gap-2" onClick={() => setShowNewLocationModal(true)}>
              <Plus className="h-4 w-4" />
              New Location
            </Button>
          )}
        </div>
        
        {/* Locations Table */}
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
              <thead className="bg-neutral-50 dark:bg-neutral-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Open Orders
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Completed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Refunded
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Avg Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Token Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Rating
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                {locationsLoading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center">
                      <div className="animate-pulse">Loading locations...</div>
                    </td>
                  </tr>
                ) : locations.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center">
                      <MapPin className="h-8 w-8 text-neutral-300 dark:text-neutral-600 mx-auto mb-2" />
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">No locations found for this vendor</p>
                    </td>
                  </tr>
                ) : (
                  locations.map((location: VendorEvent) => {
                    const totalRevenue = location.total || location.revenue || location.aggregates?.revenue || 0
                    const totalOrders = location.completed_orders || location.aggregates?.completed_orders || 0
                    const avgOrderValue = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0
                    
                    return (
                      <tr key={location.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-900">
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                          <div className="flex flex-col">
                            <Link
                              to="/events/$eventId/vendors/$vendorId/locations/$locationId"
                              params={{ eventId, vendorId, locationId: String(location.id) }}
                              className="text-blue-600 dark:text-blue-500 hover:text-blue-800 dark:hover:text-blue-400 font-medium transition-colors cursor-pointer"
                            >
                              {location.booth || location.name || 'N/A'}
                            </Link>
                            {!location.is_approved && (
                              <span className="text-xs text-orange-600 dark:text-orange-400">Pending Approval</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                          0
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                          {totalOrders.toLocaleString()}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                          {(location.aggregates?.refunded_orders || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                          {avgOrderValue}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                          {totalRevenue.toLocaleString()}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm">
                          <LocationRatingCell vendorEventId={location.id} />
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right text-sm">
                          {canEditVendors && (
                            <button
                              className="text-neutral-400 hover:text-blue-600 dark:text-neutral-500 dark:hover:text-blue-400 transition-colors"
                              title="Edit location"
                              onClick={(e) => {
                                e.stopPropagation()
                                setEditingLocation(location)
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      )}

      {/* Vendor Staff Tab Content */}
      {activeTab === 'staff' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">Vendor Staff</h2>
          {canAddVendorAdmin && (
            <Button className="gap-2" onClick={() => setShowAddStaffModal(true)}>
              <UserPlus className="h-4 w-4" />
              Add Vendor Staff
            </Button>
          )}
        </div>
        
        {/* Staff Table */}
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg">
          <div className="overflow-x-auto rounded-lg">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
              <thead className="bg-neutral-50 dark:bg-neutral-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                {administrators.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
                      No Vendor Administrators Found
                    </td>
                  </tr>
                ) : (
                  administrators.map((admin: VendorAdministrator) => (
                    <VendorStaffRow
                      key={admin.id}
                      admin={admin}
                      vendorId={vendorIdNum}
                      eventId={eventIdNum}
                      canEdit={canEditVendors}
                      canChange={canChangeVendorAdmin}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      )}

      {/* Item Reporting Tab Content */}
      {activeTab === 'reporting' && (
        <div>
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">Item Reporting</h2>
            <DateFilter
              value={{ gte: reportFrom || undefined, lte: reportTo || undefined }}
              onChange={(v) => { setReportFrom(v.gte ?? ''); setReportTo(v.lte ?? '') }}
              eventTimeZone={eventData?.timezone}
              defaultMonth={eventData?.start_date_time ? new Date(eventData.start_date_time) : undefined}
            />
          </div>

        {sortedItems.length > 0 ? (
          <div className={cn(
            'bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden',
            reportFiltering && 'opacity-60',
          )}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
                <thead className="bg-neutral-50 dark:bg-neutral-900">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-200 select-none"
                      onClick={() => handleSort('item_name')}
                    >
                      <span className="inline-flex items-center">
                        Item Name
                        <SortIcon field="item_name" />
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-200 select-none"
                      onClick={() => handleSort('booth')}
                    >
                      <span className="inline-flex items-center">
                        Location
                        <SortIcon field="booth" />
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-200 select-none"
                      onClick={() => handleSort('order_count')}
                    >
                      <span className="inline-flex items-center justify-end">
                        Orders
                        <SortIcon field="order_count" />
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-200 select-none"
                      onClick={() => handleSort('quantity_sold')}
                    >
                      <span className="inline-flex items-center justify-end">
                        Sold
                        <SortIcon field="quantity_sold" />
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-200 select-none"
                      onClick={() => handleSort('token_revenue')}
                    >
                      <span className="inline-flex items-center justify-end">
                        Token Sales
                        <SortIcon field="token_revenue" />
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-200 select-none"
                      onClick={() => handleSort('cash_revenue')}
                    >
                      <span className="inline-flex items-center justify-end">
                        Cash Sales
                        <SortIcon field="cash_revenue" />
                      </span>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Rating
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                  {sortedItems.map((item) => (
                    <tr key={item.item_id} className="hover:bg-neutral-50 dark:hover:bg-neutral-900">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                          {item.item_name}
                        </div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">
                          {item.item_price} tokens
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to="/events/$eventId/vendors/$vendorId/locations/$locationId"
                          params={{ eventId, vendorId, locationId: String(item.vendor_event_id) }}
                          className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          {item.booth}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-100 text-right">
                        {item.order_count.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-100 text-right font-medium">
                        {item.quantity_sold.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-100 text-right">
                        {item.token_revenue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-100 text-right font-medium">
                        ${item.cash_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <VendorItemRatingDisplay ratings={vendorItemRatingsMap.get(item.item_id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Totals row */}
                <tfoot className="bg-neutral-50 dark:bg-neutral-900">
                  <tr className="font-semibold">
                    <td className="px-6 py-3 text-sm text-neutral-900 dark:text-neutral-100">
                      Total ({sortedItems.length} items)
                    </td>
                    <td className="px-6 py-3 text-sm text-neutral-500 dark:text-neutral-400">
                      —
                    </td>
                    <td className="px-6 py-3 text-sm text-neutral-900 dark:text-neutral-100 text-right">
                      {sortedItems.reduce((sum, item) => sum + item.order_count, 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-sm text-neutral-900 dark:text-neutral-100 text-right">
                      {sortedItems.reduce((sum, item) => sum + item.quantity_sold, 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-sm text-neutral-900 dark:text-neutral-100 text-right">
                      {sortedItems.reduce((sum, item) => sum + item.token_revenue, 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-sm text-neutral-900 dark:text-neutral-100 text-right">
                      ${sortedItems.reduce((sum, item) => sum + item.cash_revenue, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-8 text-center">
            <BarChart2 className="h-12 w-12 text-neutral-400 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">No Item Data Available</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {reportDateParams
                ? 'No item sales in the selected date range.'
                : 'Item reporting data will appear here once orders have been placed.'}
            </p>
          </div>
        )}
        </div>
      )}

      {/* Reviews Tab Content */}
      {activeTab === 'reviews' && (
        <VendorReviewsTab vendorId={vendorIdNum} eventId={eventIdNum} />
      )}

      {/* Add Staff Modal */}
      {showAddStaffModal && (
        <AddVendorStaffModal
          vendorId={vendorIdNum}
          eventId={eventIdNum}
          onClose={() => setShowAddStaffModal(false)}
        />
      )}

      {/* Edit Vendor Modal */}
      {showEditVendorModal && vendor && (
        <EditVendorModal
          vendor={vendor}
          onClose={() => setShowEditVendorModal(false)}
        />
      )}

      {/* Edit Location Modal */}
      {editingLocation && (
        <EditLocationModal
          location={vendor ? { ...editingLocation, vendor } : editingLocation}
          eventId={eventIdNum}
          onClose={() => setEditingLocation(null)}
        />
      )}

      {/* New Location Modal — vendor pre-locked to this vendor */}
      {showNewLocationModal && vendor && (
        <NewLocationModal
          eventId={eventIdNum}
          vendors={[vendor]}
          lockedVendor={vendor}
          onClose={() => setShowNewLocationModal(false)}
        />
      )}

      {/* Export Login PDF Modal */}
      {canExportVendorLogins && showExportLoginPdfModal && (
        <ExportLoginPdfModal
          eventId={eventIdNum}
          vendors={vendor ? [vendor] : []}
          vendorEvents={locations}
          onClose={() => setShowExportLoginPdfModal(false)}
        />
      )}
    </div>
  )
}

// Vendor Staff Row Component
function VendorStaffRow({ admin, vendorId, eventId, canEdit, canChange }: { admin: VendorAdministrator; vendorId: number; eventId: number; canEdit: boolean; canChange: boolean }) {
  const [showDropdown, setShowDropdown] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, openUpward: false })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const deleteAdmin = useDeleteVendorAdministrator()
  const sendInvite = useSendActivationCode()

  const handleToggleDropdown = () => {
    if (!showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const openUpward = spaceBelow < 100 // If less than 100px below, open upward
      
      setDropdownPosition({
        top: openUpward ? rect.top - 8 : rect.bottom + 4,
        left: rect.right - 192, // 192px = w-48
        openUpward,
      })
    }
    setShowDropdown(!showDropdown)
  }

  // The backend only returns a user's activation code to platform staff, so
  // Studio asks it to e-mail the activation link to the invitee instead of
  // building the link itself.
  const handleResendInvite = () => {
    const email = admin.user.email
    sendInvite.mutate(email, {
      onSuccess: (result) => {
        if (result.success) {
          alert(`Invite e-mailed to ${email}`)
        } else {
          alert(result.error || 'Failed to send invite')
        }
      },
      onError: (error) => {
        alert(error instanceof Error ? error.message : 'Failed to send invite')
      },
    })
  }

  const handleDelete = () => {
    if (confirm(`Are you sure you want to remove ${admin.user.first_name} ${admin.user.last_name} from this vendor?`)) {
      deleteAdmin.mutate({ adminId: admin.id, vendorId, eventId })
    }
  }

  const rawRoleName = admin.groups && admin.groups.length > 0 ? admin.groups[0].name : admin.role || 'Owner'
  const roleName = formatGroupName(rawRoleName)

  return (
    <tr className="hover:bg-neutral-50 dark:hover:bg-neutral-900">
      <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-neutral-900 dark:text-neutral-100">
        {admin.user.first_name} {admin.user.last_name}
      </td>
      <td className="px-6 py-2 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
        {admin.user.email}
      </td>
      <td className="px-6 py-2 whitespace-nowrap text-sm">
        <div className="flex items-center gap-2">
          {admin.user.is_active ? (
            <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 px-2.5 py-0.5 text-xs font-medium">
              Active
            </span>
          ) : (
            <>
              <span className="inline-flex items-center rounded-full bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 px-2.5 py-0.5 text-xs font-medium">
                Inactive
              </span>
              <button
                onClick={handleResendInvite}
                disabled={sendInvite.isPending}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={`E-mail the activation link to ${admin.user.email}`}
              >
                <Send className="w-3 h-3 mr-1" />
                {sendInvite.isPending ? 'Sending…' : 'Resend invite'}
              </button>
            </>
          )}
        </div>
      </td>
      <td className="px-6 py-2 whitespace-nowrap text-sm">
        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
          {roleName}
        </span>
      </td>
      <td className="px-6 py-2 whitespace-nowrap text-sm">
        {canEdit && (
          <div className="relative">
            <button 
              ref={buttonRef}
              onClick={handleToggleDropdown}
              className="inline-flex items-center px-3 py-1 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors"
            >
              Edit
              <ChevronDown className="w-3 h-3 ml-1" />
            </button>
            
            {showDropdown && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
                <div 
                  className="fixed w-48 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-50 py-1"
                  style={{ 
                    top: dropdownPosition.openUpward ? 'auto' : dropdownPosition.top,
                    bottom: dropdownPosition.openUpward ? `${window.innerHeight - dropdownPosition.top}px` : 'auto',
                    left: dropdownPosition.left,
                  }}
                >
                  {canChange && (
                    <button
                      onClick={() => { setShowDropdown(false); setShowEditModal(true); }}
                      className="w-full flex items-center px-3 py-2 text-left text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit Permissions
                    </button>
                  )}
                  {!admin.user.is_active && (
                    <button
                      onClick={() => { setShowDropdown(false); setShowChangeEmailModal(true); }}
                      className="w-full flex items-center px-3 py-2 text-left text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Change Invite Email
                    </button>
                  )}
                  <button
                    onClick={() => { setShowDropdown(false); handleDelete(); }}
                    className="w-full flex items-center px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </td>

      {/* Edit Vendor Admin Modal */}
      {showEditModal && (
        <EditVendorAdminModal
          admin={admin}
          vendorId={vendorId}
          eventId={eventId}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {/* Change Invite Email Modal */}
      {showChangeEmailModal && (
        <ChangeInviteEmailModal
          admin={admin}
          vendorId={vendorId}
          eventId={eventId}
          onClose={() => setShowChangeEmailModal(false)}
        />
      )}
    </tr>
  )
}

// Change Invite Email Modal Component
// Re-invites a pending vendor admin at a different address. Studio cannot copy
// activation links any more (the backend only e-mails them), so when the invite
// is bouncing or spam-filtered at the original address this is the way to get
// the person in. The new row is created first and the old pending row removed
// second, so a failed create leaves the original untouched.
function ChangeInviteEmailModal({ admin, vendorId, eventId, onClose }: { admin: VendorAdministrator; vendorId: number; eventId: number; onClose: () => void }) {
  const oldEmail = admin.user.email
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const createAdmin = useCreateVendorAdministrator()
  const deleteAdmin = useDeleteVendorAdministrator()
  const isPending = createAdmin.isPending || deleteAdmin.isPending

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const newEmail = email.trim()
    if (!newEmail) {
      setError('Email is required')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setError('Enter a valid email address')
      return
    }
    if (newEmail.toLowerCase() === oldEmail.toLowerCase()) {
      setError('That is already the invite address')
      return
    }

    // Same role and locations as the pending row
    const groups = admin.groups?.map(g => g.id) || []
    const vendorEventIds = admin.vendor_event_administrators
      ?.map(vea => typeof vea.vendor_event === 'object' ? vea.vendor_event.id : vea.vendor_event)
      .filter((id): id is number => typeof id === 'number')

    let created: VendorAdministrator
    try {
      created = await createAdmin.mutateAsync({
        email: newEmail,
        vendor: vendorId,
        groups,
        ...(vendorEventIds !== undefined ? { vendor_event_ids: vendorEventIds } : {}),
        event_id: eventId,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send the new invite')
      return
    }

    // If the address already belongs to an active account the backend attaches
    // it and sends an "added" e-mail instead of an invite. The create response
    // only carries the user object when expanded, so fall back to "invited".
    const createdUser = typeof created.user === 'object' && created.user !== null ? created.user : null
    const sentMessage = createdUser?.is_active
      ? `${newEmail} already has an active account, so it was given access instead of an invite.`
      : `Invite sent to ${newEmail}.`

    try {
      await deleteAdmin.mutateAsync({ adminId: admin.id, vendorId, eventId })
    } catch (err) {
      setError(`${sentMessage} The pending row for ${oldEmail} could not be removed (${err instanceof Error ? err.message : 'unknown error'}). Remove it manually.`)
      return
    }

    alert(`${sentMessage} The pending invite for ${oldEmail} was removed from this vendor.`)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={isPending ? undefined : onClose} />
      <div className="relative bg-white dark:bg-neutral-900 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b border-neutral-200 dark:border-neutral-800">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Change Invite Email</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Send this invite to a different address</p>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-4 space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-900/50">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Current invite address
              </label>
              <Input type="email" value={oldEmail} readOnly disabled />
            </div>

            <div>
              <label htmlFor="change-invite-email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                New email address
              </label>
              <Input
                id="change-invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                autoFocus
                disabled={isPending}
              />
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                The same role and locations carry over. The activation email goes to the new address and the pending invite for the old one is removed from this vendor.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-4 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Sending...' : 'Send Invite'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Add Vendor Staff Modal Component
function AddVendorStaffModal({ vendorId, eventId, onClose }: { vendorId: number; eventId: number; onClose: () => void }) {
  const [email, setEmail] = useState('')
  const [groupId, setGroupId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [selectedVendorEventIds, setSelectedVendorEventIds] = useState<number[]>([])
  const [initialized, setInitialized] = useState(false)
  
  const { data: groupsData, isLoading: groupsLoading } = usePermissionGroups()
  // Filter vendor events by both vendor AND event
  const { data: vendorEventsData, isLoading: vendorEventsLoading } = useVendorLocations(vendorId, eventId)
  const createAdmin = useCreateVendorAdministrator()
  
  const groups = groupsData?.results || []
  const vendorEvents = vendorEventsData?.results || []

  // Initialize all vendor events as selected by default
  useEffect(() => {
    if (!initialized && vendorEvents.length > 0) {
      setSelectedVendorEventIds(vendorEvents.map(ve => ve.id))
      setInitialized(true)
    }
  }, [vendorEvents, initialized])

  const handleToggleVendorEvent = (vendorEventId: number) => {
    setSelectedVendorEventIds(prev => 
      prev.includes(vendorEventId)
        ? prev.filter(id => id !== vendorEventId)
        : [...prev, vendorEventId]
    )
  }

  const handleSelectAll = () => {
    setSelectedVendorEventIds(vendorEvents.map(ve => ve.id))
  }

  const handleDeselectAll = () => {
    setSelectedVendorEventIds([])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!email.trim()) {
      setError('Email is required')
      return
    }
    if (!groupId) {
      setError('Permission group is required')
      return
    }
    if (selectedVendorEventIds.length === 0) {
      setError('At least one location must be selected')
      return
    }

    try {
      await createAdmin.mutateAsync({
        email: email.trim(),
        vendor: vendorId,
        groups: [parseInt(groupId)],
        vendor_event_ids: selectedVendorEventIds,
        event_id: eventId,
      })
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to add vendor staff')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-neutral-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-neutral-200 dark:border-neutral-800">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Add Vendor Staff</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Add a user to manage this vendor</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-900/50">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="staff@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Permission Group <span className="text-red-500">*</span>
              </label>
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className="w-full h-10 px-3 border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a group...</option>
                {groupsLoading ? (
                  <option disabled>Loading...</option>
                ) : (
                  groups
                    .filter((group) => group.name.startsWith('vendor_'))
                    .map((group) => {
                      const displayName = group.name
                        .split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')
                      return (
                        <option key={group.id} value={group.id}>
                          {displayName}
                        </option>
                      )
                    })
                )}
              </select>
            </div>

            {/* Vendor Events / Locations Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Locations <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <button 
                    type="button" 
                    onClick={handleSelectAll}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Select All
                  </button>
                  <span className="text-neutral-300 dark:text-neutral-600">|</span>
                  <button 
                    type="button" 
                    onClick={handleDeselectAll}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              
              {vendorEventsLoading ? (
                <div className="text-sm text-neutral-500 py-4 text-center">Loading locations...</div>
              ) : vendorEvents.length === 0 ? (
                <div className="text-sm text-neutral-500 py-4 text-center border border-dashed border-neutral-200 dark:border-neutral-700 rounded-md">
                  No locations found for this vendor
                </div>
              ) : (
                <div className="border border-neutral-200 dark:border-neutral-700 rounded-md max-h-48 overflow-y-auto">
                  {vendorEvents.map((vendorEvent) => (
                    <label 
                      key={vendorEvent.id}
                      className="flex items-center p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 cursor-pointer border-b border-neutral-100 dark:border-neutral-800 last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedVendorEventIds.includes(vendorEvent.id)}
                        onChange={() => handleToggleVendorEvent(vendorEvent.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                          {vendorEvent.name || vendorEvent.booth || `Location ${vendorEvent.id}`}
                        </div>
                        {typeof vendorEvent.event === 'object' && vendorEvent.event !== null && (vendorEvent.event as any).name ? (
                          <div className="text-xs text-neutral-500 dark:text-neutral-400">
                            {(vendorEvent.event as any).name}
                          </div>
                        ) : null}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                {selectedVendorEventIds.length} of {vendorEvents.length} locations selected
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-4 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createAdmin.isPending}>
              {createAdmin.isPending ? 'Adding...' : 'Add Staff'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Edit Vendor Admin Modal Component
function EditVendorAdminModal({ admin, vendorId, eventId, onClose }: { admin: VendorAdministrator; vendorId: number; eventId: number; onClose: () => void }) {
  const initialVendorEventIds = (admin.vendor_event_administrators || [])
    .map(vea => typeof vea.vendor_event === 'object' ? vea.vendor_event.id : vea.vendor_event)
    .filter((id): id is number => typeof id === 'number')

  const [selectedGroups, setSelectedGroups] = useState<number[]>(
    admin.groups?.map(g => g.id) || []
  )
  const [selectedVendorEventIds, setSelectedVendorEventIds] = useState<number[]>(initialVendorEventIds)
  const [error, setError] = useState<string | null>(null)

  const { data: groupsData, isLoading: groupsLoading } = usePermissionGroups()
  const { data: vendorEventsData, isLoading: vendorEventsLoading } = useVendorLocations(vendorId, eventId)
  const updateAdmin = useUpdateVendorAdministrator()

  const groups = groupsData?.results || []
  const vendorGroups = groups.filter((g: PermissionGroup) => g.name.startsWith('vendor_'))
  const vendorEvents = vendorEventsData?.results || []

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (selectedGroups.length === 0) {
      setError('At least one permission group is required')
      return
    }
    if (selectedVendorEventIds.length === 0) {
      setError('At least one location must be selected')
      return
    }

    try {
      await updateAdmin.mutateAsync({
        adminId: admin.id,
        vendorId,
        data: { groups: selectedGroups, vendor_event_ids: selectedVendorEventIds, event_id: eventId },
      })
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to update permissions')
    }
  }

  const toggleGroup = (groupId: number) => {
    setSelectedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    )
  }

  const toggleVendorEvent = (vendorEventId: number) => {
    setSelectedVendorEventIds(prev =>
      prev.includes(vendorEventId)
        ? prev.filter(id => id !== vendorEventId)
        : [...prev, vendorEventId]
    )
  }

  const handleSelectAllLocations = () => {
    setSelectedVendorEventIds(vendorEvents.map(ve => ve.id))
  }

  const handleDeselectAllLocations = () => {
    setSelectedVendorEventIds([])
  }

  const formatGroupName = (name: string) => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-neutral-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Edit Vendor Administrator</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Update permissions and locations for this user</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-800 dark:text-red-200 text-sm">
              {error}
            </div>
          )}

          {/* User Info (Read-only) */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Name
              </label>
              <input
                type="text"
                value={`${admin.user.first_name || ''} ${admin.user.last_name || ''}`.trim() || 'N/A'}
                disabled
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Email
              </label>
              <input
                type="email"
                value={admin.user.email || 'N/A'}
                disabled
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 cursor-not-allowed"
              />
            </div>
          </div>
          
          {/* Permission Groups (Editable) */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Permission Groups <span className="text-red-500">*</span>
            </label>
            {groupsLoading ? (
              <div className="text-sm text-neutral-500">Loading groups...</div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {vendorGroups.map((group: PermissionGroup) => (
                  <label
                    key={group.id}
                    className="flex items-center p-3 border border-neutral-200 dark:border-neutral-700 rounded-md cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  >
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(group.id)}
                      onChange={() => toggleGroup(group.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                    />
                    <span className="ml-3 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {formatGroupName(group.name)}
                    </span>
                  </label>
                ))}
              </div>
            )}
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
              Select one or more permission groups
            </p>
          </div>

          {/* Locations (Editable) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Locations <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSelectAllLocations}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Select All
                </button>
                <span className="text-neutral-300 dark:text-neutral-600">|</span>
                <button
                  type="button"
                  onClick={handleDeselectAllLocations}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Deselect All
                </button>
              </div>
            </div>

            {vendorEventsLoading ? (
              <div className="text-sm text-neutral-500 py-4 text-center">Loading locations...</div>
            ) : vendorEvents.length === 0 ? (
              <div className="text-sm text-neutral-500 py-4 text-center border border-dashed border-neutral-200 dark:border-neutral-700 rounded-md">
                No locations found for this vendor
              </div>
            ) : (
              <div className="border border-neutral-200 dark:border-neutral-700 rounded-md max-h-48 overflow-y-auto">
                {vendorEvents.map((vendorEvent) => (
                  <label
                    key={vendorEvent.id}
                    className="flex items-center p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 cursor-pointer border-b border-neutral-100 dark:border-neutral-800 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedVendorEventIds.includes(vendorEvent.id)}
                      onChange={() => toggleVendorEvent(vendorEvent.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {vendorEvent.name || vendorEvent.booth || `Location ${vendorEvent.id}`}
                      </div>
                      {typeof vendorEvent.event === 'object' && vendorEvent.event !== null && (vendorEvent.event as any).name ? (
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">
                          {(vendorEvent.event as any).name}
                        </div>
                      ) : null}
                    </div>
                  </label>
                ))}
              </div>
            )}
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              {selectedVendorEventIds.length} of {vendorEvents.length} locations selected
            </p>
          </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 p-4 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isLoading={updateAdmin.isPending}>
              Update Permissions
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Edit Vendor Modal Component
interface EditVendorModalProps {
  vendor: Vendor
  onClose: () => void
}

type EditVendorTab = 'details' | 'branding' | 'users' | 'locations'

function EditVendorModal({ vendor, onClose }: EditVendorModalProps) {
  const { eventId } = Route.useParams()
  const updateVendor = useUpdateVendor()
  const [activeTab, setActiveTab] = useState<EditVendorTab>('details')
  const [showAddStaffModal, setShowAddStaffModal] = useState(false)

  const [formData, setFormData] = useState({
    name: vendor.name || '',
    description: vendor.description || '',
    website: vendor.website || '',
  })
  const [error, setError] = useState<string | null>(null)
  const [brandingError, setBrandingError] = useState<string | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(vendor.logo || null)
  const [backgroundPreview, setBackgroundPreview] = useState<string | null>(
    (vendor as Vendor & { background?: string }).background || null
  )
  const [removeLogo, setRemoveLogo] = useState(false)
  const [removeBackground, setRemoveBackground] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const backgroundInputRef = useRef<HTMLInputElement>(null)

  // Update form when vendor changes
  useEffect(() => {
    setFormData({
      name: vendor.name || '',
      description: vendor.description || '',
      website: vendor.website || '',
    })
    setLogoPreview(vendor.logo || null)
    setBackgroundPreview((vendor as Vendor & { background?: string }).background || null)
    setLogoFile(null)
    setBackgroundFile(null)
    setRemoveLogo(false)
    setRemoveBackground(false)
  }, [vendor])

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      setBrandingError('Logo must be 2MB or smaller')
      return
    }
    setBrandingError(null)
    setLogoFile(file)
    setRemoveLogo(false)
    const reader = new FileReader()
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  const handleBackgroundUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      setBrandingError('Background must be 5MB or smaller')
      return
    }
    setBrandingError(null)
    setBackgroundFile(file)
    setRemoveBackground(false)
    const reader = new FileReader()
    reader.onload = (ev) => setBackgroundPreview(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  const handleRemoveLogo = (e: React.MouseEvent) => {
    e.stopPropagation()
    setLogoFile(null)
    setLogoPreview(null)
    setRemoveLogo(true)
    if (logoInputRef.current) logoInputRef.current.value = ''
  }

  const handleRemoveBackground = (e: React.MouseEvent) => {
    e.stopPropagation()
    setBackgroundFile(null)
    setBackgroundPreview(null)
    setRemoveBackground(true)
    if (backgroundInputRef.current) backgroundInputRef.current.value = ''
  }

  const handleSubmitDetails = async () => {
    setError(null)

    if (!formData.name.trim()) {
      setError('Vendor name is required')
      return
    }

    try {
      await updateVendor.mutateAsync({
        vendorId: vendor.id,
        data: {
          name: formData.name.trim(),
          description: formData.description.trim(),
          website: formData.website.trim(),
        },
      })
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to update vendor')
    }
  }

  const handleSubmitBranding = async () => {
    setBrandingError(null)
    if (!logoFile && !backgroundFile && !removeLogo && !removeBackground) {
      setBrandingError('Choose a logo or background to upload, or remove an existing one')
      return
    }
    try {
      await updateVendor.mutateAsync({
        vendorId: vendor.id,
        data: {
          logo: logoFile ? logoFile : removeLogo ? null : undefined,
          background: backgroundFile ? backgroundFile : removeBackground ? null : undefined,
        },
      })
      setLogoFile(null)
      setBackgroundFile(null)
      setRemoveLogo(false)
      setRemoveBackground(false)
    } catch (err: any) {
      setBrandingError(err.message || 'Failed to update branding')
    }
  }

  const tabs: { id: EditVendorTab; label: string; icon: React.ReactNode }[] = [
    {
      id: 'details',
      label: 'Vendor Details',
      icon: <Pencil className="h-5 w-5 mr-3" />,
    },
    {
      id: 'branding',
      label: 'Branding',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-3">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      ),
    },
    {
      id: 'users',
      label: 'Users',
      icon: <Users className="h-5 w-5 mr-3" />,
    },
    {
      id: 'locations',
      label: 'Locations',
      icon: <MapPin className="h-5 w-5 mr-3" />,
    },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div 
        className="relative bg-white dark:bg-neutral-900 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Edit Vendor</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">{vendor.name}</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content with sidebar */}
        <div className="flex flex-col md:flex-row max-h-[calc(90vh-5rem)] overflow-hidden">
          {/* Sidebar navigation */}
          <div className="w-full md:w-56 border-b md:border-b-0 md:border-r border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950">
            <nav className="p-2 md:p-4">
              <ul className="space-y-1">
                {tabs.map((tab) => (
                  <li key={tab.id}>
                    <button
                      onClick={() => setActiveTab(tab.id)}
                      className={cn(
                        'w-full flex items-center p-3 text-left rounded-lg transition-colors text-xs sm:text-sm',
                        activeTab === tab.id
                          ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 border-l-4 border-blue-600 dark:border-blue-500'
                          : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 border-l-4 border-transparent'
                      )}
                    >
                      {tab.icon}
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Main content area */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Vendor Details Tab */}
            {activeTab === 'details' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Vendor Details</h3>
                  
                  {error && (
                    <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-900/50">
                      {error}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                        Vendor Name *
                      </label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter vendor name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Vendor description"
                        className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={4}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                        Website URL
                      </label>
                      <Input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        placeholder="https://example.com"
                      />
                    </div>

                    <div className="pt-4">
                      <Button onClick={handleSubmitDetails} disabled={updateVendor.isPending}>
                        {updateVendor.isPending ? 'Updating...' : 'Update Vendor Details'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Branding Tab */}
            {activeTab === 'branding' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Vendor Branding</h3>

                {brandingError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-900/50">
                    {brandingError}
                  </div>
                )}

                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Upload Logo
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      className="w-full border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg p-8 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                    >
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo preview" className="h-20 mx-auto object-contain mb-3" />
                      ) : (
                        <Upload className="h-10 w-10 mx-auto text-neutral-400 mb-3" />
                      )}
                      <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                        {logoPreview ? 'Change Logo' : 'Upload'}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">PNG, JPG, SVG, WebP up to 2MB</p>
                    </button>
                    {logoPreview && (
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        aria-label="Remove logo"
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-800 shadow-sm transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>

                {/* Background Upload */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Upload Background
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => backgroundInputRef.current?.click()}
                      className="w-full border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg p-8 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                    >
                      {backgroundPreview ? (
                        <img src={backgroundPreview} alt="Background preview" className="h-20 w-full object-cover rounded mb-3" />
                      ) : (
                        <Upload className="h-10 w-10 mx-auto text-neutral-400 mb-3" />
                      )}
                      <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                        {backgroundPreview ? 'Change Background' : 'Upload'}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">JPG, PNG, WebP up to 5MB</p>
                    </button>
                    {backgroundPreview && (
                      <button
                        type="button"
                        onClick={handleRemoveBackground}
                        aria-label="Remove background"
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-800 shadow-sm transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <input
                    ref={backgroundInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleBackgroundUpload}
                    className="hidden"
                  />
                </div>

                {/* Preview */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Preview
                  </label>
                  <div
                    className="relative bg-neutral-100 dark:bg-neutral-800 rounded-lg aspect-video flex items-center justify-center overflow-hidden"
                    style={backgroundPreview ? {
                      backgroundImage: `url(${backgroundPreview})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    } : {}}
                  >
                    {backgroundPreview && <div className="absolute inset-0 bg-black/20" />}
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo on background" className="relative max-h-24 max-w-[60%] object-contain drop-shadow-lg" />
                    ) : (
                      !backgroundPreview && (
                        <div className="text-center">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-12 w-12 mx-auto text-neutral-400 mb-2">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400">Upload logo</p>
                        </div>
                      )
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                    This preview shows how your logo will appear on the background
                  </p>
                </div>

                <div className="pt-4">
                  <Button
                    onClick={handleSubmitBranding}
                    disabled={updateVendor.isPending || (!logoFile && !backgroundFile)}
                  >
                    {updateVendor.isPending ? 'Updating...' : 'Update Branding'}
                  </Button>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Vendor Users</h3>
                  <Button className="gap-2" onClick={() => setShowAddStaffModal(true)}>
                    <UserPlus className="h-4 w-4" />
                    Add Vendor Staff
                  </Button>
                </div>
                
                <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
                    <thead className="bg-neutral-50 dark:bg-neutral-900">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Role</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                      {vendor.administrators && vendor.administrators.length > 0 ? (
                        vendor.administrators.map((admin: VendorAdministrator) => (
                          <tr key={admin.id}>
                            <td className="px-4 py-3 text-sm text-neutral-900 dark:text-neutral-100">
                              {admin.user?.first_name} {admin.user?.last_name}
                            </td>
                            <td className="px-4 py-3 text-sm text-neutral-500 dark:text-neutral-400">
                              {admin.user?.email}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                                {admin.groups?.[0]?.name?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Staff'}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="px-4 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
                            No users assigned to this vendor
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Locations Tab */}
            {activeTab === 'locations' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Vendor Locations</h3>
                </div>
                
                <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
                    <thead className="bg-neutral-50 dark:bg-neutral-900">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                      {vendor.vendorevent_set && vendor.vendorevent_set.length > 0 ? (
                        vendor.vendorevent_set.map((location: VendorEvent) => (
                          <tr key={location.id}>
                            <td className="px-4 py-3">
                              <Link
                                to="/events/$eventId/vendors/$vendorId/locations/$locationId"
                                params={{ eventId, vendorId: String(vendor.id), locationId: String(location.id) }}
                                className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                onClick={onClose}
                              >
                                {location.booth || location.name}
                              </Link>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={cn(
                                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                                location.is_approved 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                              )}>
                                {location.is_approved ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-neutral-900 dark:text-neutral-100">
                              {(location.aggregates?.tokens_spent || location.tokens_spent || 0).toLocaleString()} tokens
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="px-4 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
                            No locations for this vendor
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Manage vendor locations from the Vendor Locations section on the vendor page.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Vendor Staff Modal - renders on top of Edit Vendor Modal */}
      {showAddStaffModal && (
        <AddVendorStaffModal
          vendorId={vendor.id}
          eventId={Number(eventId)}
          onClose={() => setShowAddStaffModal(false)}
        />
      )}
    </div>
  )
}

// Vendor Reviews Tab Component
function VendorReviewsTab({ vendorId, eventId }: { vendorId: number; eventId: number }) {
  const { data: orderSummary, isLoading: orderSummaryLoading } = useOrderReviewSummary({ vendor: vendorId, event: eventId })
  const { data: itemSummary, isLoading: itemSummaryLoading } = useItemReviewSummary({ vendor: vendorId, event: eventId })
  const { data: itemReviewsData, isLoading: itemReviewsLoading } = useItemReviews({
    vendor: vendorId,
    event: eventId,
    expand: 'vendor_event_item',
    page_size: 200,
  })

  const itemReviewAggregates = useMemo(() => {
    if (!itemReviewsData?.results) return []
    const map = new Map<number, { name: string; totalRatings: number; scaleSum: number; scaleCount: number; thumbsUp: number; thumbsDown: number }>()
    for (const review of itemReviewsData.results) {
      const item = typeof review.vendor_event_item === 'object' ? review.vendor_event_item : null
      if (!item) continue
      let agg = map.get(item.id)
      if (!agg) {
        agg = { name: item.name, totalRatings: 0, scaleSum: 0, scaleCount: 0, thumbsUp: 0, thumbsDown: 0 }
        map.set(item.id, agg)
      }
      for (const cr of review.criterion_ratings) {
        agg.totalRatings++
        if (cr.scale_rating != null) { agg.scaleSum += cr.scale_rating; agg.scaleCount++ }
        if (cr.thumbs_up === true) agg.thumbsUp++
        if (cr.thumbs_up === false) agg.thumbsDown++
      }
    }
    return Array.from(map.entries()).map(([id, agg]) => ({ id, ...agg }))
  }, [itemReviewsData])

  const isLoading = orderSummaryLoading || itemSummaryLoading

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
              <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-20 mb-2" />
              <div className="h-6 bg-neutral-200 dark:bg-neutral-800 rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const totalOrderReviews = orderSummary?.total_reviews || 0
  const scaleAvg = orderSummary?.scale_avg || 0
  const thumbsUpPct = orderSummary?.thumbs_up_percentage || 0
  const totalItemReviews = itemSummary?.total_reviews || 0

  if (totalOrderReviews === 0 && totalItemReviews === 0) {
    return (
      <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-12 text-center">
        <Star className="h-12 w-12 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">No Reviews Yet</h3>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Reviews will appear here once customers start leaving feedback.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-blue-100 dark:bg-blue-900/30 mr-3">
              <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{totalOrderReviews}</p>
              <p className="text-xs text-neutral-600 dark:text-neutral-400">Order Reviews</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-yellow-100 dark:bg-yellow-900/30 mr-3">
              <Star className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                {scaleAvg > 0 ? `${scaleAvg.toFixed(1)}` : '—'}
              </p>
              <p className="text-xs text-neutral-600 dark:text-neutral-400">Avg Rating</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-green-100 dark:bg-green-900/30 mr-3">
              <ThumbsUp className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                {thumbsUpPct > 0 ? `${thumbsUpPct.toFixed(0)}%` : '—'}
              </p>
              <p className="text-xs text-neutral-600 dark:text-neutral-400">Positive</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-purple-100 dark:bg-purple-900/30 mr-3">
              <Star className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{totalItemReviews}</p>
              <p className="text-xs text-neutral-600 dark:text-neutral-400">Item Reviews</p>
            </div>
          </div>
        </div>
      </div>

      {/* Item Review Criteria Breakdown */}
      {(itemSummary?.criteria_summary?.length || 0) > 0 && (
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg">
          <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Item Review Criteria</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-900/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Criterion</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Result</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Responses</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {itemSummary!.criteria_summary.map((criterion) => (
                  <tr key={criterion.criterion_id} className="hover:bg-neutral-50 dark:hover:bg-neutral-900/30">
                    <td className="px-4 py-3 text-sm font-medium text-neutral-900 dark:text-neutral-100">{criterion.label}</td>
                    <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-400 capitalize">{criterion.input_type}</td>
                    <td className="px-4 py-3 text-center">
                      {criterion.input_type === 'scale' ? (
                        <div className="flex items-center justify-center gap-1">
                          <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{(criterion.scale_avg || 0).toFixed(1)}</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <ThumbsUp className="h-4 w-4 text-green-500 fill-green-500" />
                          <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{(criterion.thumbs_up_percentage || 0).toFixed(0)}%</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-600 dark:text-neutral-400">
                      {criterion.input_type === 'scale'
                        ? criterion.scale_count || 0
                        : (criterion.thumbs_up_count || 0) + (criterion.thumbs_down_count || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reviewed Items */}
      <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Reviewed Items</h3>
        </div>
        {itemReviewsLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto" />
          </div>
        ) : !itemReviewAggregates.length ? (
          <div className="p-8 text-center text-neutral-500 dark:text-neutral-400 text-sm">
            No item reviews yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-900/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Item</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Rating</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Reviews</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {itemReviewAggregates.map((item) => (
                  <tr key={item.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-900/30">
                    <td className="px-4 py-3 text-sm font-medium text-neutral-900 dark:text-neutral-100">{item.name}</td>
                    <td className="px-4 py-3 text-center">
                      {item.scaleCount > 0 ? (
                        <div className="flex items-center justify-center gap-1">
                          <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{(item.scaleSum / item.scaleCount).toFixed(1)}</span>
                        </div>
                      ) : (item.thumbsUp + item.thumbsDown) > 0 ? (
                        <div className="flex items-center justify-center gap-1">
                          <ThumbsUp className="h-4 w-4 text-green-500 fill-green-500" />
                          <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{((item.thumbsUp / (item.thumbsUp + item.thumbsDown)) * 100).toFixed(0)}%</span>
                        </div>
                      ) : (
                        <span className="text-sm text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-600 dark:text-neutral-400">{item.totalRatings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// Location Rating Cell - fetches review summary for a single vendor_event
function LocationRatingCell({ vendorEventId }: { vendorEventId: number }) {
  const { data: summary, isLoading } = useOrderReviewSummary({ vendor_event: vendorEventId })

  if (isLoading) {
    return <span className="text-neutral-400 dark:text-neutral-600">...</span>
  }

  if (!summary || summary.total_reviews === 0) {
    return <span className="text-neutral-400 dark:text-neutral-600">—</span>
  }

  // Show scale avg if available, otherwise thumbs up %
  if (summary.scale_count > 0) {
    return (
      <div className="flex items-center gap-1">
        <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400" />
        <span className="text-neutral-700 dark:text-neutral-300 font-medium">{summary.scale_avg.toFixed(1)}</span>
        <span className="text-neutral-400 dark:text-neutral-500 text-xs">({summary.total_reviews})</span>
      </div>
    )
  }

  if ((summary.thumbs_up_count + summary.thumbs_down_count) > 0) {
    return (
      <div className="flex items-center gap-1">
        <ThumbsUp className="h-3.5 w-3.5 text-green-500 fill-green-500" />
        <span className="text-neutral-700 dark:text-neutral-300 font-medium">{summary.thumbs_up_percentage.toFixed(0)}%</span>
        <span className="text-neutral-400 dark:text-neutral-500 text-xs">({summary.total_reviews})</span>
      </div>
    )
  }

  return <span className="text-neutral-400 dark:text-neutral-600">—</span>
}

// Displays aggregated rating for a vendor item in item reporting table
function VendorItemRatingDisplay({ ratings }: { ratings?: { totalRatings: number; scaleSum: number; scaleCount: number; thumbsUp: number; thumbsDown: number } }) {
  if (!ratings || ratings.totalRatings === 0) {
    return <span className="text-neutral-400 dark:text-neutral-600">—</span>
  }

  if (ratings.scaleCount > 0) {
    const avg = ratings.scaleSum / ratings.scaleCount
    return (
      <div className="flex items-center justify-center gap-1">
        <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400" />
        <span className="text-neutral-700 dark:text-neutral-300 font-medium">{avg.toFixed(1)}</span>
        <span className="text-neutral-400 dark:text-neutral-500 text-xs">({ratings.totalRatings})</span>
      </div>
    )
  }

  if (ratings.thumbsUp + ratings.thumbsDown > 0) {
    const pct = (ratings.thumbsUp / (ratings.thumbsUp + ratings.thumbsDown)) * 100
    return (
      <div className="flex items-center justify-center gap-1">
        <ThumbsUp className="h-3.5 w-3.5 text-green-500 fill-green-500" />
        <span className="text-neutral-700 dark:text-neutral-300 font-medium">{pct.toFixed(0)}%</span>
        <span className="text-neutral-400 dark:text-neutral-500 text-xs">({ratings.totalRatings})</span>
      </div>
    )
  }

  return <span className="text-neutral-400 dark:text-neutral-600">—</span>
}
